import type { GovernedProject } from "@esi/shared";
import { useQuery } from "@tanstack/react-query";
import { Hourglass } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatBytes, formatNumber, formatUsd } from "@/components/charts";
import { Notice } from "@/components/Notice";
import { PageHeader } from "@/components/PageHeader";
import { Skeleton } from "@/components/ui/skeleton";
import { ok } from "../api-errors.ts";
import { formatDateTime, formatShortDate } from "../i18n/format.ts";
import { Link } from "../router.tsx";
import { MyAccessRequests, pendingFor, useMyAccessRequests } from "./AccessRequests.tsx";
import { useAdminApi } from "./admin-api.tsx";
import { FigureTable } from "./reports/parts.tsx";
import { useScopeEntities } from "./reports/queries.ts";

/**
 * Projects the admin governs (spec `projects` "Governance figures"): metadata, aggregates and the
 * last 30 days from the usage rollup. Personal projects (tenant admins only) are never named.
 */
/** A 30-day figure; null for personal projects (not reported one by one). */
const figure = (value: number | null, format: (n: number) => string) =>
  value === null ? "—" : format(value);

export function GovernedProjects() {
  const { admin, base } = useAdminApi();
  const { t } = useTranslation();
  const projects = useQuery({
    queryKey: ["admin", "projects"],
    queryFn: async (): Promise<GovernedProject[]> => ok(await admin.projects.$get({ query: {} })),
    retry: false,
  });
  const entities = useScopeEntities();
  const mine = useMyAccessRequests();
  const entityName = new Map((entities.data ?? []).map((e) => [e.id as string, e.name]));
  return (
    <div data-esi-content="projects" className="flex flex-col gap-6">
      <PageHeader title={t("governance.title")} lead={t("governance.lead")} />
      {projects.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : projects.isError ? (
        <Notice tone="danger">{projects.error.message}</Notice>
      ) : projects.data.length === 0 ? (
        <p className="text-sm text-fg-muted">{t("governance.empty")}</p>
      ) : (
        <section aria-labelledby="governed-projects" className="flex flex-col gap-2">
          <h2 id="governed-projects" className="text-xs font-medium text-fg-muted">
            {t("governance.last30")}
          </h2>
          <FigureTable
            compact
            caption={t("governance.title")}
            // The 30-day figures first, so they stay in view on a narrow screen.
            columns={[
              t("governance.name"),
              t("governance.flowDenials"),
              t("governance.questions"),
              t("governance.activeUsers"),
              t("governance.cost"),
              t("governance.owner"),
              t("governance.members"),
              t("governance.documents"),
              t("governance.lastActivity"),
            ]}
            align={["end", "end", "end", "end", "start", "end", "end", "end"]}
            rows={projects.data.map((p) => ({
              key: p.id,
              cells: [
                // A floor under the name, so a narrow column never stacks it over three lines.
                <span
                  key="name"
                  className="inline-flex min-w-40 flex-wrap items-center gap-x-2 gap-y-0.5"
                >
                  {p.name === null ? (
                    t("governance.personal")
                  ) : (
                    // Entity projects open their governance view; personal ones stay a row.
                    <Link
                      href={`${base}/projects/${p.id}`}
                      className="font-medium text-fg underline decoration-line-strong underline-offset-4 hover:decoration-current"
                    >
                      {p.name}
                    </Link>
                  )}
                  {p.frozenAt ? (
                    <span className="rounded border border-line px-1.5 text-xs text-fg-muted">
                      {t("governance.frozen")}
                    </span>
                  ) : null}
                  {pendingFor(mine.data, p.id).length > 0 ? (
                    <span className="inline-flex items-center gap-1 text-xs text-fg-muted">
                      <Hourglass aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
                      {t("governance.pending.inList")}
                    </span>
                  ) : null}
                </span>,
                figure(p.flowDenials30d, formatNumber),
                figure(p.questions30d, formatNumber),
                figure(p.activeUsers30d, formatNumber),
                figure(p.costUsd30d, formatUsd),
                <Owner
                  key="owner"
                  name={
                    p.owner.kind === "entity"
                      ? (entityName.get(p.owner.entityId) ?? "—")
                      : t("governance.personalOwner")
                  }
                />,
                formatNumber(p.members),
                // The count and the space they take share a column, as on the project's own view.
                <span key="documents">
                  {formatNumber(Object.values(p.documents).reduce((n, c) => n + (c ?? 0), 0))}
                  {" · "}
                  {formatBytes(p.storageBytes)}
                </span>,
                p.lastActivityAt ? (
                  <time
                    key="last"
                    dateTime={p.lastActivityAt}
                    title={formatDateTime(p.lastActivityAt)}
                  >
                    {formatShortDate(p.lastActivityAt)}
                  </time>
                ) : (
                  "—"
                ),
              ],
            }))}
          />
        </section>
      )}
      <MyAccessRequests />
    </div>
  );
}

/**
 * The owning entity, cut to one line in a dense table (wider once the table has room); the full
 * name stays in the text (screen readers read it whole) and shows on hover.
 */
function Owner(props: { name: string }) {
  return (
    <span className="block max-w-28 truncate @5xl:max-w-56" title={props.name}>
      {props.name}
    </span>
  );
}
